/**
 * Flooring Calculator V2
 * Supports two modes:
 *   - pack: user enters m2, calculator outputs packs needed
 *   - linear: user enters strekkende meters, calculator outputs m2 coverage (0.1m unit approach)
 */

class FlooringCalculator extends HTMLElement {
  constructor() {
    super();
    this._abortController = null;
    this._linearUnits = 0;
  }

  connectedCallback() {
    this._abortController = new AbortController();
    this._initElements();
    this._bindEvents();
  }

  disconnectedCallback() {
    if (this._abortController) {
      this._abortController.abort();
    }
  }

  // --- Data attribute getters ---

  get pakinhoud()         { return parseFloat(this.dataset.pakinhoud) || 0; }
  get wasteMode()         { return this.dataset.wasteMode || 'percentage'; }
  get wastePercent()      { return parseInt(this.dataset.wastePercent, 10) || 10; }
  get wasteCm()           { return parseInt(this.dataset.wasteCm, 10) || 10; }
  get price()             { return parseInt(this.dataset.displayPrice, 10) || parseInt(this.dataset.price, 10) || 0; }
  get comparePrice()      { return parseInt(this.dataset.comparePrice, 10) || 0; }
  get compareAtPrice()    { return parseInt(this.dataset.compareAtPrice, 10) || 0; }
  get variantPrice()      { return parseInt(this.dataset.variantPrice, 10) || 0; }
  get pricePerLm()        { return parseInt(this.dataset.pricePerLm, 10) || 0; }
  get breedte()           { return parseInt(this.dataset.breedte, 10) || 0; }
  get unitsPerMeter()     { return parseInt(this.dataset.unitsPerMeter, 10) || 1; }
  get showWasteBreakdown(){ return this.dataset.showWasteBreakdown === 'true'; }
  get currency()          { return this.dataset.currency || 'EUR'; }
  get productTitle()      { return this.dataset.productTitle || ''; }
  get variantId()         { return this.dataset.variantId; }
  get formId()            { return this.dataset.formId; }
  get mode()              { return this.dataset.mode || 'pack'; }
  get unitName()          { return this.dataset.unitName || 'pakken'; }
  get hasDiscount()       { return this.dataset.hasDiscount === 'true'; }
  get discountPercent()   { return parseInt(this.dataset.discountPercent, 10) || 0; }
  get stuksTotalNote()    { return this.dataset.stuksTotalNote || '{stuks} stuks à {length}m, totaal'; }
  get stuksPriceNote()    { return this.dataset.stuksPriceNote || '({stuks} stuks à {length}m)'; }
  get cutInclWaste()      { return this.dataset.cutInclWaste || '(incl. snijverlies)'; }

  _fillTemplate(template, stuks, lengthText) {
    return template.replace('{stuks}', String(stuks)).replace('{length}', lengthText);
  }

  // --- Waste calculation (shared by both modes) ---

  _applyWaste(value) {
    return this.wasteMode === 'flat_cm'
      ? value + (this.wasteCm / 100)
      : value * (1 + this.wastePercent / 100);
  }

  _getWasteLabel() {
    return this.wasteMode === 'flat_cm'
      ? `${this.wasteCm} cm`
      : `${this.wastePercent}%`;
  }

  _getStuks() {
    const stuks = Number(this._stuksInput?.value);
    return Number.isInteger(stuks) && stuks > 0 ? stuks : 1;
  }

  // --- Element references ---

  _initElements() {
    this._m2Input = this.querySelector('[data-m2-input]');
    this._packsInput = this.querySelector('[data-packs-input]');
    this._infoBox = this.querySelector('[data-info-box]');
    this._infoText = this.querySelector('[data-info-text]');
    this._priceSummary = this.querySelector('[data-price-summary]');
    this._calculateButton = this.querySelector('[data-calculate-button]');
    this._addToCartButton = this.querySelector('[data-add-to-cart]');
    this._quantityInput = this.querySelector('[data-quantity-input]');
    this._afmetingInput = this.querySelector('[data-afmeting-input]');
    this._stuksInput = this.querySelector('[data-stuks-input]');
    this._stuksPropInput = this.querySelector('[data-stuks-prop-input]');
    this._lengtePerStukInput = this.querySelector('[data-lengte-per-stuk-input]');
    this._opgegevenInput = this.querySelector('[data-opgegeven-input]');
    this._snijplanInput = this.querySelector('[data-snijplan-input]');

    if (this._priceSummary) {
      this._productLine = this._priceSummary.querySelector('[data-product-line]');
      this._linePrice = this._priceSummary.querySelector('[data-line-price]');
      this._wasteRow = this._priceSummary.querySelector('[data-waste-row]');
      this._wasteLine = this._priceSummary.querySelector('[data-waste-line]');
      this._wastePrice = this._priceSummary.querySelector('[data-waste-price]');
      this._savingsAmount = this._priceSummary.querySelector('[data-savings-amount]');
      this._totalPrice = this._priceSummary.querySelector('[data-total-price]');
      this._totalSavings = this._priceSummary.querySelector('[data-total-savings]');
    }
  }

  // --- Event binding ---

  _bindEvents() {
    const signal = this._abortController.signal;

    if (this._m2Input) {
      this._m2Input.addEventListener('input', () => this._calculate(), { signal });
      this._m2Input.addEventListener('focus', (e) => {
        if (e.target.value === '0') e.target.select();
      }, { signal });
    }

    if (this._stuksInput) {
      this._stuksInput.addEventListener('input', () => {
        if (this.mode === 'linear' && this._parseDecimal(this._m2Input?.value) > 0) this._calculate();
      }, { signal });
    }

    if (this._calculateButton) {
      this._calculateButton.addEventListener('click', () => {
        if (this._m2Input) {
          this._m2Input.focus();
          this._m2Input.select();
        }
      }, { signal });
    }

    if (this._addToCartButton) {
      this._addToCartButton.addEventListener('click', () => this._addToCart(), { signal });
    }

    document.addEventListener('variant:change', (e) => this._handleVariantChange(e), { signal });
  }

  // --- Main calculation dispatcher ---

  _parseDecimal(value) {
    return parseFloat(String(value).replace(',', '.')) || 0;
  }

  _calculate() {
    if (!this._m2Input || this.pakinhoud <= 0) return;

    const inputValue = this._parseDecimal(this._m2Input.value);

    if (inputValue <= 0) {
      if (this._packsInput) this._packsInput.value = 0;
      this._hideResults();
      return;
    }

    if (this.mode === 'linear') {
      this._calculateLinear(inputValue);
    } else {
      this._calculatePack(inputValue);
    }
  }

  // --- Pack mode calculation ---

  _calculatePack(m2Value) {
    if (!this._packsInput) return;

    const m2WithWaste = this._applyWaste(m2Value);
    const packsNeeded = Math.ceil(m2WithWaste / this.pakinhoud);
    const actualCoverage = packsNeeded * this.pakinhoud;

    this._packsInput.value = packsNeeded;

    const pricePerPack = this.variantPrice / 100;
    const totalPrice = packsNeeded * pricePerPack;
    const pricePerM2 = pricePerPack / this.pakinhoud;
    const productPrice = m2Value * pricePerM2;
    const wasteCost = totalPrice - productPrice;

    const comparePricePerPack = this.compareAtPrice / 100;
    const totalComparePrice = (this.hasDiscount && comparePricePerPack > 0) ? packsNeeded * comparePricePerPack : 0;
    const savings = totalComparePrice > 0 ? totalComparePrice - totalPrice : 0;

    // Info box
    this._setInfoBox(
      `Je hebt <strong>${packsNeeded} ${this.unitName} nodig</strong> voor ${actualCoverage.toFixed(2)} m2 (dit is inclusief ${this._getWasteLabel()} snijverlies)`
    );

    // Price summary
    const wasteM2 = actualCoverage - m2Value;
    this._setPriceSummary({
      productText: `${packsNeeded} ${this.unitName} ${this.productTitle} a ${this._formatPrice(pricePerM2)} per m2 voor ${m2Value.toFixed(2)} m2`,
      totalText: `${packsNeeded} ${this.unitName} ${this.productTitle} a ${this._formatPrice(pricePerM2)} per m2 voor ${actualCoverage.toFixed(2)} m2`,
      productPrice: productPrice,
      wasteText: `${wasteM2.toFixed(2)} m2 snijverlies`,
      wasteCost: wasteCost,
      total: totalPrice,
      savings: savings,
    });

    // Form values
    this._setFormValues({ quantity: packsNeeded, coverage: actualCoverage });
  }

  // --- Linear mode calculation ---

  _calculateLinear(linearMeters) {
    const breedteM = this.pakinhoud;
    const stuks = this._getStuks();
    const metersWithWaste = this._applyWaste(linearMeters);
    const unitsPerPiece = Math.round(metersWithWaste * this.unitsPerMeter);
    const cutMetersPerPiece = unitsPerPiece / this.unitsPerMeter;
    const units = unitsPerPiece * stuks;
    const actualMeters = units / this.unitsPerMeter;
    const coverage = actualMeters * breedteM;

    this._linearUnits = units;

    const pricePerUnit = this.variantPrice / 100;
    const totalPrice = units * pricePerUnit;
    const pricePerM2 = this.price / 100;

    const inputUnits = Math.round(linearMeters * this.unitsPerMeter) * stuks;
    const productPrice = inputUnits * pricePerUnit;
    const wasteCost = totalPrice - productPrice;
    const wasteMeters = actualMeters - (inputUnits / this.unitsPerMeter);

    const comparePricePerUnit = this.compareAtPrice / 100;
    const totalComparePrice = (this.hasDiscount && comparePricePerUnit > 0) ? units * comparePricePerUnit : 0;
    const savings = totalComparePrice > 0 ? totalComparePrice - totalPrice : 0;

    const fmt = (m) => m % 1 === 0 ? m.toString() : m.toFixed(1);
    const cutLengthText = fmt(cutMetersPerPiece);
    const stuksNote = stuks > 1 ? ' ' + this._fillTemplate(this.stuksPriceNote, stuks, cutLengthText) : '';
    const stuksTotalPrefix = stuks > 1 ? this._fillTemplate(this.stuksTotalNote, stuks, cutLengthText) + ' ' : '';

    // Info box
    this._setInfoBox(
      `Het door jou berekende oppervlak is <strong>${coverage.toFixed(2)} m2</strong> (${stuksTotalPrefix}lengte ${fmt(actualMeters)}m x breedte ${breedteM}m, inclusief ${this._getWasteLabel()} snijverlies)`
    );

    // Price summary
    this._setPriceSummary({
      productText: `${this.productTitle} ${breedteM}m x ${fmt(linearMeters)}m${stuks > 1 ? ` x ${stuks}` : ''} = ${(linearMeters * breedteM * stuks).toFixed(2)} m2 (${this._formatPrice(pricePerM2)}/m2)`,
      totalText: `${this.productTitle} ${breedteM}m x ${fmt(actualMeters)}m${stuksNote} = ${coverage.toFixed(2)} m2 (${this._formatPrice(pricePerM2)}/m2)`,
      productPrice: productPrice,
      wasteText: `${fmt(wasteMeters)}m snijverlies (${(wasteMeters * breedteM).toFixed(2)} m2)`,
      wasteCost: wasteCost,
      total: totalPrice,
      savings: savings,
    });

    // Form values
    this._setFormValues({
      quantity: units,
      coverage: coverage,
      meters: actualMeters,
      cutMetersPerPiece: cutMetersPerPiece,
      givenMeters: linearMeters,
      stuks: stuks,
    });
  }

  // --- Shared UI update methods ---

  _setInfoBox(html) {
    if (this._infoBox && this._infoText) {
      this._infoText.innerHTML = html;
      this._infoBox.hidden = false;
    }
  }

  _setPriceSummary({ productText, totalText, productPrice, wasteText, wasteCost, total, savings }) {
    if (!this._priceSummary) return;

    if (this._wasteRow && this.showWasteBreakdown && wasteCost > 0) {
      if (this._productLine) this._productLine.textContent = productText;
      if (this._linePrice) this._linePrice.textContent = this._formatPrice(productPrice);
      if (this._wasteLine) this._wasteLine.textContent = wasteText;
      if (this._wastePrice) this._wastePrice.textContent = this._formatPrice(wasteCost);
      this._wasteRow.hidden = false;
    } else {
      if (this._productLine) this._productLine.textContent = totalText;
      if (this._linePrice) this._linePrice.textContent = this._formatPrice(total);
      if (this._wasteRow) this._wasteRow.hidden = true;
    }

    if (this._savingsAmount && savings > 0) this._savingsAmount.textContent = this._formatPrice(savings);
    if (this._totalPrice) this._totalPrice.textContent = this._formatPrice(total);
    if (this._totalSavings && savings > 0) this._totalSavings.textContent = this._formatPrice(savings);

    this._priceSummary.hidden = false;

    if (this._calculateButton) this._calculateButton.hidden = true;
  }

  _setFormValues({ quantity, coverage, meters, cutMetersPerPiece, givenMeters, stuks }) {
    if (this._quantityInput) this._quantityInput.value = quantity;

    if (this._afmetingInput) {
      this._afmetingInput.value = coverage.toLocaleString('nl-NL', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }) + ' m\u00B2';
    }

    if (meters !== undefined) {
      const fmtMeters = (m) => m.toLocaleString('nl-NL', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      }) + ' meter';

      if (this._lengtePerStukInput && cutMetersPerPiece !== undefined) {
        this._lengtePerStukInput.value = fmtMeters(cutMetersPerPiece) + ' ' + this.cutInclWaste;
      }

      if (this._opgegevenInput && givenMeters !== undefined) {
        this._opgegevenInput.value = fmtMeters(givenMeters);
      }

      if (this._stuksPropInput && stuks !== undefined) {
        this._stuksPropInput.value = String(stuks);
      }
    }
  }

  _hideResults() {
    if (this._infoBox) this._infoBox.hidden = true;
    if (this._priceSummary) this._priceSummary.hidden = true;
    if (this._calculateButton) this._calculateButton.hidden = false;
    if (this._afmetingInput) this._afmetingInput.value = '';
    if (this._lengtePerStukInput) this._lengtePerStukInput.value = '';
    if (this._opgegevenInput) this._opgegevenInput.value = '';
    if (this._stuksPropInput) this._stuksPropInput.value = '';
    this._linearUnits = 0;
  }

  // --- Utilities ---

  _formatPrice(amount) {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: this.currency
    }).format(amount);
  }

  // --- Cart integration ---

  _addToCart() {
    let quantity;

    if (this.mode === 'linear') {
      quantity = this._linearUnits || 0;
    } else {
      quantity = parseInt(this._packsInput?.value, 10) || 0;
    }

    if (quantity <= 0) {
      if (this._m2Input) this._m2Input.focus();
      return;
    }

    const productForm = document.getElementById(this.formId);
    if (!productForm) return;

    productForm.id.disabled = false;
    productForm.id.value = this.variantId;
    productForm.id.dispatchEvent(new Event('change', { bubbles: true }));

    if (this._quantityInput) {
      this._quantityInput.value = quantity;
    }

    if (this.mode === 'linear') {
      if (this._stuksPropInput) {
        this._stuksPropInput.value = String(this._getStuks());
      }

      if (this._snijplanInput) {
        this._snijplanInput.value = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
      }
    }

    productForm.requestSubmit();
  }

  // --- Variant change handler ---

  _handleVariantChange(event) {
    const variant = event.detail?.variant;
    if (!variant) return;

    this.dataset.variantId = variant.id;
    this.dataset.variantPrice = variant.price;
    this.dataset.compareAtPrice = variant.compare_at_price || 0;

    if (this._m2Input && this._parseDecimal(this._m2Input.value) > 0) {
      this._calculate();
    }
  }
}

if (!window.customElements.get('flooring-calculator')) {
  window.customElements.define('flooring-calculator', FlooringCalculator);
}
